import React, { useEffect, useState } from "react"
import { Backdrop, CircularProgress, Container, Grid, Snackbar, Switch } from "@material-ui/core"
import { Alert } from "@material-ui/lab"
import backdropStyles from '../AgentSkillsManagement/styles.module.css'
import formStyles from '../TeamsManagement/styles.module.css'
import mainStyles from '../WorkerManagementComponent/WorkerManagementComponent.module.css'
import styles from './styles.module.css'
import { BlueBtn, GreyInverseBtn } from "../Styled/StyledButtons"
import { CarrierBlocksActions } from "../../actions/CarrierBlocksActions"
import { ICarrierSettingsResponse } from "../../models/CarrierBlocks/CarrierRule"

/**
 * Carrier Blocker settings.
 *
 * Three things this screen has to say out loud, because getting any of them from
 * documentation instead has already gone wrong once on the robocall side:
 *
 *   - what ATTACK mode additionally blocks, in the UI rather than in a spec. Google Voice and
 *     Twilio SMS numbers carry real customers — a 7.69% lead rate outside an attack — so the
 *     button cannot look like a bigger version of the safe one;
 *   - how long the current mode has been running. Attack mode is temporary by design and
 *     nothing else reminds anybody to leave it, which is exactly how block_hours_first sat at
 *     a non-preset value for a day;
 *   - that a comment is required, before the save is attempted rather than after it fails.
 *
 * Scale warning, from the 9-day replay: light mode flags roughly HALF of everything reaching
 * the call centre on a weekday, 61% on the worst day. An operator who expects a marginal
 * filter will read the first day as an outage.
 */
const CarrierSettings = () => {
    const [data, setData] = useState<ICarrierSettingsResponse | null>(null)
    const [values, setValues] = useState<{ [k: string]: any }>({})
    const [comment, setComment] = useState('')
    const [backdrop, setBackdrop] = useState(true)
    const [saving, setSaving] = useState(false)
    const [alert, setAlert] = useState<{ severity: 'error' | 'success' | 'warning' | 'info', text: string } | null>(null)

    const load = () => {
        CarrierBlocksActions.getSettings()
            .then((res: ICarrierSettingsResponse) => {
                setData(res)
                setValues({ ...res.values })
                setBackdrop(false)
            })
            .catch((error) => {
                setBackdrop(false)
                setAlert({ severity: 'error', text: describe(error) })
            })
    }

    useEffect(load, [])

    const describe = (error: any): string => {
        const d = error?.response?.data
        if (d?.messages) {
            return Object.keys(d.messages).map((k) => d.messages[k]).join(' ')
        }
        return d?.error || error?.message || 'Request failed'
    }

    // Only what the operator actually moved is sent, so a stale field on this screen cannot
    // overwrite something changed elsewhere in the meantime.
    const changed = (): { payload: any, count: number } => {
        const payload: any = {}
        let count = 0
        if (data === null) { return { payload, count } }

        Object.keys(data.values).forEach((key) => {
            const before = (data.values as any)[key]
            const now = values[key]
            const norm = (v: any) => typeof v === 'boolean' ? (v ? 1 : 0) : v
            if (String(norm(before)) !== String(norm(now))) {
                payload[key] = norm(now)
                count++
            }
        })

        return { payload, count }
    }

    const { payload, count: changeCount } = changed()

    const save = () => {
        if (changeCount === 0) {
            setAlert({ severity: 'warning', text: 'Nothing changed.' })
            return
        }
        if (comment.trim().length < 4) {
            setAlert({ severity: 'error', text: 'A comment is required.' })
            return
        }

        setSaving(true)
        CarrierBlocksActions.updateSettings(payload, comment.trim())
            .then(() => {
                setSaving(false)
                setComment('')
                setAlert({ severity: 'success', text: 'Saved.' })
                load()
            })
            .catch((error) => {
                setSaving(false)
                setAlert({ severity: 'error', text: describe(error) })
            })
    }

    const setValue = (key: string, value: any) => setValues({ ...values, [key]: value })

    /**
     * Load a preset into the form. Deliberately does NOT save: the change count and the
     * required comment still apply, so a preset cannot become a way to start rejecting calls
     * without validation, attribution or a history row.
     */
    const applyPreset = (name: string, label: string, presetValues: any) => {
        setValues({ ...values, ...presetValues })
        setComment(`Carrier blocker: ${label.toLowerCase()}.`)
        setAlert({
            severity: 'warning',
            text: `"${label}" loaded into the form — review and press Save to apply.`,
        })
    }

    const stateClass = (state: string) => {
        if (state === 'attack') { return styles.StatusExpired }
        if (state === 'live') { return styles.StatusActive }
        if (state === 'dry_run') { return styles.StatusCandidate }
        return styles.StatusExpired
    }

    return (
        <>
            <Container className={mainStyles.mainContainer} maxWidth={false}>
                {data !== null ?
                    <Grid container spacing={1}>

                        {/* ------------------------------------------------ state banner */}
                        <Grid item xs={12}>
                            <div className={styles.Panel}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} md={3}>
                                        <span className={`${styles.Status} ${stateClass(data.effective_state.state)}`}>
                                            {data.effective_state.label.toUpperCase()}
                                        </span>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <div className={styles.Evidence}>{data.effective_state.detail}</div>
                                        {/* Attack mode is temporary by design; this is the only
                                            thing that says how long it has been on. */}
                                        {data.values.carrier_block_mode === 'attack'
                                            && data.mode_since.hours !== null ?
                                            <div className={styles.EvidenceWarn}>
                                                In ATTACK mode for {data.mode_since.hours} h
                                                (since {data.mode_since.since}). Switch back when
                                                volume normalises.
                                            </div>
                                            : null}
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <GreyInverseBtn fullWidth={true} href="/carrier-blocks">
                                            Back to the carrier list
                                        </GreyInverseBtn>
                                    </Grid>
                                </Grid>
                            </div>
                        </Grid>

                        {/* ------------------------------------------------ scale warning */}
                        <Grid item xs={12}>
                            <div className={styles.Panel}>
                                <Alert severity="info">
                                    <b>This is not a marginal filter.</b> Replayed over nine days
                                    of the 2026-07-27…08-04 attack, <b>light</b> mode flags about
                                    half of everything reaching the call centre on a weekday —
                                    61% on the worst day — at a 66.5% junk share. Expect roughly
                                    860 verdicts a day. Judge it on a rolling weekday average of
                                    ≥65% junk, never on one day: the per-day figure ranged
                                    51.7–75.0% and weekends are meaningless (23 and 5 calls).
                                </Alert>
                            </div>
                        </Grid>

                        {/* ------------------------------------------------ presets */}
                        <Grid item xs={12}>
                            <div className={styles.Panel}>
                                <h4>Modes</h4>
                                <div className={styles.Hint}>
                                    A preset fills the form below; nothing is saved until you add
                                    a comment and press Save.
                                </div>
                                <Grid container spacing={2}>
                                    {Object.keys(data.presets || {}).map((name) => {
                                        const preset = data.presets[name]
                                        const isCurrent = Object.keys(preset.values).every((k) =>
                                            String(values[k] === true ? 1 : values[k] === false ? 0 : values[k])
                                            === String(preset.values[k]))
                                        const isAttack = String(preset.values['carrier_block_mode']) === 'attack'
                                        return (
                                            <Grid item xs={12} md={6} key={name}>
                                                {isCurrent ?
                                                    <GreyInverseBtn fullWidth={true}
                                                        onClick={() => applyPreset(name, preset.label, preset.values)}>
                                                        {preset.label} — current
                                                    </GreyInverseBtn>
                                                    :
                                                    <BlueBtn fullWidth={true}
                                                        onClick={() => applyPreset(name, preset.label, preset.values)}>
                                                        Load “{preset.label}”
                                                    </BlueBtn>
                                                }
                                                <div className={styles.Hint}>{preset.help}</div>
                                                {/* Required by spec §6.1: the warning belongs on
                                                    the button, not in a document nobody reads
                                                    during an incident. */}
                                                {isAttack ?
                                                    <div className={styles.EvidenceWarn}>
                                                        Also blocks Google Voice and Twilio SMS
                                                        numbers. These carry real customers —
                                                        7.69% lead rate outside attacks. Use
                                                        during an attack and switch back.
                                                    </div>
                                                    : null}
                                            </Grid>
                                        )
                                    })}
                                </Grid>
                            </div>
                        </Grid>

                        {/* ------------------------------------------------ switches */}
                        <Grid item xs={12}>
                            <div className={styles.Panel}>
                                <h4>Switches</h4>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={3}>
                                        <label className={styles.Evidence}>
                                            <Switch
                                                checked={!!values.carrier_block_active}
                                                color="primary"
                                                onChange={(e) => setValue('carrier_block_active', e.target.checked)}
                                            />
                                            <b>Blocker active</b>
                                        </label>
                                        <div className={styles.Hint}>
                                            Off means the carrier of an incoming call is not even
                                            looked at.
                                        </div>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <label className={styles.Evidence}>
                                            <Switch
                                                checked={!!values.carrier_block_dry_run}
                                                color="primary"
                                                onChange={(e) => setValue('carrier_block_dry_run', e.target.checked)}
                                            />
                                            <b>Dry run</b>
                                        </label>
                                        <div className={styles.Hint}>
                                            Record which calls WOULD be rejected and let every one
                                            through. Leave on for a full weekday first — the cost
                                            figures on the stats screen only exist in dry run,
                                            because a rejected call never reaches an agent to be
                                            classified.
                                        </div>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <label className={formStyles.FormLabel}>Mode</label>
                                        <select
                                            className={formStyles.FormControl}
                                            value={values.carrier_block_mode}
                                            onChange={(e) => setValue('carrier_block_mode', e.target.value)}
                                        >
                                            <option value="light">light — wholesale only</option>
                                            <option value="attack">attack — also consumer carriers</option>
                                        </select>
                                        <div className={styles.Hint}>
                                            Enforced tiers: {(data.enforced_tiers || []).join(', ')}
                                        </div>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <label className={formStyles.FormLabel}>Rule cache TTL (s)</label>
                                        <input
                                            type="number"
                                            min={30}
                                            max={3600}
                                            className={formStyles.FormControl}
                                            value={values.carrier_block_cache_ttl}
                                            onChange={(e) => setValue('carrier_block_cache_ttl',
                                                e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                                        />
                                        <div className={styles.Hint}>
                                            Admin edits bust the cache immediately; this only bounds
                                            a change made straight in the database.
                                        </div>
                                    </Grid>
                                </Grid>
                            </div>
                        </Grid>

                        {/* ------------------------------------------------ save */}
                        <Grid item xs={12}>
                            <div className={styles.Panel}>
                                <Grid container spacing={2} alignItems="flex-end">
                                    <Grid item xs={12} md={7}>
                                        <label className={formStyles.FormLabel}>Comment (required)</label>
                                        <input
                                            placeholder="Why are you changing this?"
                                            className={formStyles.FormControl}
                                            value={comment}
                                            onChange={(e) => setComment(e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <div className={styles.Evidence}>
                                            {changeCount} change{changeCount === 1 ? '' : 's'}
                                        </div>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <BlueBtn
                                            fullWidth={true}
                                            disabled={saving || changeCount === 0 || comment.trim().length < 4}
                                            onClick={save}
                                        >
                                            {saving ? 'Saving…' : 'Save settings'}
                                        </BlueBtn>
                                    </Grid>
                                </Grid>
                            </div>
                        </Grid>

                        {/* ------------------------------------------------ history */}
                        <Grid item xs={12}>
                            <div className={styles.Panel}>
                                <h4>Recent spam-side changes</h4>
                                <div className={styles.Hint}>
                                    One audit trail for every spam switch, carrier and robocall
                                    alike — which is what makes an incident reconstructable.
                                </div>
                                {(data.history || []).length === 0 ?
                                    <div className={styles.Empty}>No changes recorded yet.</div>
                                    :
                                    data.history.map((row) => (
                                        <div className={styles.HistoryRow} key={row.id}>
                                            <span className={styles.HistoryAction}>{row.created_at}</span>
                                            {' — '}
                                            {row.user?.email || (row.user_id ? `user ${row.user_id}` : 'system')}
                                            {' — '}
                                            {Object.keys(row.changes || {}).map((k) =>
                                                `${k}: ${JSON.stringify(row.changes[k].from)} → ${JSON.stringify(row.changes[k].to)}`
                                            ).join(' · ')}
                                            <div className={styles.EvidenceMuted}>{row.comment}</div>
                                        </div>
                                    ))
                                }
                            </div>
                        </Grid>
                    </Grid>
                    : <></>}
            </Container>

            <Snackbar
                open={alert !== null}
                autoHideDuration={6000}
                onClose={() => setAlert(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Alert onClose={() => setAlert(null)} severity={alert?.severity || 'error'} variant="filled">
                    <div className={backdropStyles.AlertError}>{alert?.text}</div>
                </Alert>
            </Snackbar>

            <Backdrop className={backdropStyles.Backdrop} open={backdrop}>
                <CircularProgress className={backdropStyles.BackdropProgress} />&nbsp;&nbsp;
                <p className={backdropStyles.BackdropProgress}>Loading...</p>
            </Backdrop>
        </>
    )
}

export default CarrierSettings
